import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationsBell() {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get('/alerts', { params: { acknowledged: false } });
      setAlerts(data.slice(0, 10));
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const unread = alerts.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notifications-button" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500/90 text-[10px] font-semibold flex items-center justify-center">
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold">{t('recent_alerts')}</div>
          <Badge variant="outline" className="text-[10px]">{unread}</Badge>
        </div>
        <ScrollArea className="h-[320px]">
          {alerts.length === 0 ? (
            <div className="p-6 text-center text-sm text-[hsl(var(--muted-foreground))]">
              {t('no_data')}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {alerts.map((a) => (
                <li key={a.id} className="p-3 hover:bg-accent/40" data-testid={`notification-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${a.severity === 'high' ? 'bg-red-400' : 'bg-amber-400'} animate-pulse`} />
                    <span className="text-xs font-semibold">{a.service_name || 'BOMM'}</span>
                    <span className="ml-auto text-[10px] text-[hsl(var(--muted-foreground))] font-mono">
                      {a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ''}
                    </span>
                  </div>
                  <div className="text-sm mt-1 leading-snug">{a.message}</div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
